import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOnboarding } from "@/context/OnboardingContext";
import OptimizedPhotoUploader from "./OptimizedPhotoUploader";
import PhotoGallery from "./PhotoGallery";
import PhotoUploadButton from "./PhotoUploadButton";
import { SkipPhotosDialog } from "./SkipPhotosDialog";
import { debugLogger } from '@/utils/debugLogger';
import { showErrorToast, showValidationErrorToast } from '@/utils/toastUtils';
import { useStepValidation } from '@/hooks/useStepValidation';
import { MINIMUM_PHOTOS_REQUIRED } from '@/core/BusinessRulesEngine';
import StickyOnboardingFooter from "./StickyOnboardingFooter";

const PhotosStep = () => {
  const { 
    data, 
    updateData, 
    saveOnboardingData, 
    loading,
    registerPhotoUploadCleanup
  } = useOnboarding();
  
  const { validateAndSaveStep, isValidating } = useStepValidation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSkipPhotosDialog, setShowSkipPhotosDialog] = useState(false);
  
  // Ref to store the photo uploader cleanup function
  const photoUploaderCleanupRef = useRef<(() => void) | null>(null);

  // Use only the photos from onboarding context with null safety
  const contextPhotos = data?.photos || [];

  // Track if there's active upload work (for button disable)
  const [hasActiveUploadWork, setHasActiveUploadWork] = useState(false);

  // State to hold upload button props (for rendering in PhotoGallery)
  const [uploadButtonProps, setUploadButtonProps] = useState<{
    onClick: () => void;
    disabled: boolean;
  } | null>(null);

  // Register cleanup function when component mounts
  useEffect(() => {
    const cleanup = () => {
      debugLogger.debug('PhotosStep: Executing cleanup on navigation back');
      
      // Execute photo uploader cleanup if available
      if (photoUploaderCleanupRef.current) {
        try {
          photoUploaderCleanupRef.current();
          debugLogger.debug('PhotosStep: Photo uploader cleanup executed');
        } catch (error) {
          debugLogger.error('PhotosStep: Error during photo uploader cleanup:', error);
        }
      }
      
      // Additional cleanup if needed
      debugLogger.debug('PhotosStep: Full cleanup completed');
    };

    // Register the cleanup function with the onboarding context
    registerPhotoUploadCleanup(cleanup);
    
    debugLogger.debug('PhotosStep: Cleanup function registered');

    // Cleanup on component unmount
    return () => {
      debugLogger.debug('PhotosStep: Component unmounting');
    };
  }, [registerPhotoUploadCleanup]);

  // Function to register photo uploader cleanup
  const registerPhotoUploaderCleanup = (cleanupFn: () => void) => {
    photoUploaderCleanupRef.current = cleanupFn;
    debugLogger.debug('PhotosStep: Photo uploader cleanup function registered');
  };

  const handlePhotosUpdate = (newPhotos: string[]) => {
    try {
      debugLogger.debug('PhotosStep: handlePhotosUpdate called', {
        receivedPhotos: newPhotos,
        currentContextPhotos: contextPhotos,
        action: 'REPLACE'
      });
      // REPLACE photos instead of appending (hook already maintains correct state)
      updateData({ photos: newPhotos });
    } catch (error) {
      debugLogger.error('PhotosStep: Error updating photos:', error?.message || 'Unknown error');
      showErrorToast({
        title: "Erro ao adicionar fotos",
        description: "Não foi possível adicionar as fotos. Tente novamente."
      });
    }
  };

  const handlePhotosReplace = (updatedPhotos: string[]) => {
    try {
      debugLogger.debug('PhotosStep: Replacing context photos with:', updatedPhotos);
      updateData({ photos: updatedPhotos });
    } catch (error) {
      debugLogger.error('PhotosStep: Error replacing photos:', error?.message || 'Unknown error');
      showErrorToast({
        title: "Erro ao organizar fotos",
        description: "Não foi possível organizar as fotos. Tente novamente."
      });
    }
  };

  const handleFinishClick = () => {
    // Se não tem fotos suficientes, abrir Dialog
    if (contextPhotos.length < MINIMUM_PHOTOS_REQUIRED) {
      setShowSkipPhotosDialog(true);
      return;
    }
    // Se tem fotos suficientes, finalizar normalmente
    validateAndComplete();
  };

  const handleConfirmSkipPhotos = async () => {
    setShowSkipPhotosDialog(false);
    // Finalizar onboarding sem a validação de fotos mínimas
    await validateAndComplete(true);
  };

  const validateAndComplete = async (skipPhotoValidation = false) => {
    try {
      setIsProcessing(true);
      
      if (!data) {
        debugLogger.error('PhotosStep: Context data is null or undefined');
        showErrorToast({
          title: "Erro de dados",
          description: "Os dados do onboarding não foram encontrados. Recarregue a página e tente novamente."
        });
        return;
      }

      // Validate minimum photos (only if not skipping)
      if (!skipPhotoValidation && contextPhotos.length < MINIMUM_PHOTOS_REQUIRED) {
        showValidationErrorToast(
          `Você precisa adicionar pelo menos ${MINIMUM_PHOTOS_REQUIRED} fotos para continuar.`
        );
        return;
      }

      // Prepare final data with photos and main_photo_url
      const finalData = {
        ...data,
        photos: contextPhotos,
        main_photo_url: contextPhotos[0]
      };

      debugLogger.debug('PhotosStep: Final data before validation and saving:', {
        photos: finalData.photos,
        main_photo_url: finalData.main_photo_url,
        photosCount: finalData.photos?.length || 0,
        username: finalData.username,
        gender: finalData.gender,
        orientation: finalData.orientation,
        role: finalData.role
      });

      // Update context with final data first
      updateData(finalData);

      // Use the same validation pattern as other steps
      const stepSaveSuccess = await validateAndSaveStep(finalData, {
        stepNumber: 7,
        customValidation: (data) => {
          // Check required photos (minimum 3) - only if not skipping
          if (!skipPhotoValidation && (!data.photos || data.photos.length < MINIMUM_PHOTOS_REQUIRED)) {
            const photosNeeded = MINIMUM_PHOTOS_REQUIRED - (data.photos?.length || 0);
            const photoText = photosNeeded === 1 ? 'uma foto' : photosNeeded === 2 ? 'duas fotos' : 'três fotos';
            return { 
              isValid: false, 
              error: `Adicione mais ${photoText} para continuar (mínimo ${MINIMUM_PHOTOS_REQUIRED}).` 
            };
          }

          // Check required basic fields
          if (!data.username || !data.username.trim()) {
            return { isValid: false, error: "Username é obrigatório." };
          }

          if (!data.gender) {
            return { isValid: false, error: "Gênero é obrigatório." };
          }

          if (!data.orientation) {
            return { isValid: false, error: "Orientação é obrigatória." };
          }

          if (!data.role) {
            return { isValid: false, error: "Tipo de perfil é obrigatório." };
          }

          return { isValid: true };
        },
        onSuccess: async () => {
          debugLogger.debug('PhotosStep: Step 7 saved successfully, proceeding to finalize onboarding');
          
          try {
            // Now finalize the entire onboarding
            await saveOnboardingData();
            debugLogger.debug('PhotosStep: Onboarding completed successfully');
          } catch (completionError) {
            debugLogger.error('PhotosStep: Onboarding completion failed:', completionError?.message || 'Unknown completion error');
            showErrorToast({
              title: "Erro ao finalizar",
              description: completionError?.message || "Ocorreu um erro ao finalizar o onboarding. Tente novamente."
            });
          }
        },
        onError: (error, errorDetails) => {
          debugLogger.error('PhotosStep: Step validation failed:', { error, errorDetails });
        },
        showSuccessToast: false // Don't show success toast for step save, only for final completion
      });

      if (!stepSaveSuccess) {
        debugLogger.error('PhotosStep: Failed to save step 7 data');
        return;
      }

    } catch (error) {
      debugLogger.error("PhotosStep: Unexpected error during completion:", {
        error: error?.message || 'Unknown error',
        errorType: typeof error,
        errorObject: error,
        contextData: data,
        photos: contextPhotos
      });
      
      showErrorToast({
        title: "Erro inesperado",
        description: error?.message || "Ocorreu um erro inesperado. Recarregue a página e tente novamente."
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const canContinue = Boolean(
    data && 
    !loading && 
    !isProcessing &&
    !isValidating &&
    !hasActiveUploadWork
  );

  const photosRemaining = Math.max(0, MINIMUM_PHOTOS_REQUIRED - contextPhotos.length);

  return (
    <>
      <StickyOnboardingFooter
        buttonText="Finalizar Perfil"
        onClick={handleFinishClick}
        disabled={!canContinue}
        isLoading={isProcessing || isValidating}
        loadingText="Finalizando..."
      >
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-4 md:mb-8">
            <h1 className="text-xl md:text-2xl font-bold text-white mb-1 md:mb-2">
              Adicione suas fotos
            </h1>
            <p className="text-xs md:text-sm text-white/70">
              Adicione fotos que mostrem sua personalidade
            </p>
          </div>

          <div className="space-y-4 md:space-y-6">
            {/* Photos section */}
            <Card className="bg-black/40 backdrop-blur-sm border-none shadow-2xl p-4 md:p-6">
              <CardHeader className="px-0 pt-0 pb-2 md:pb-4">
                <CardTitle className="text-white text-base md:text-lg">Suas Fotos</CardTitle>
                {contextPhotos && contextPhotos.length > 0 && (
                  <p className="text-white/60 text-xs md:text-sm">
                    Você já tem {contextPhotos.length} foto(s). Adicione mais ou reorganize.
                  </p>
                )}
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="space-y-4 md:space-y-6">
                  {/* Show existing photos first */}
                  {contextPhotos && contextPhotos.length > 0 && (
                    <PhotoGallery 
                      photos={contextPhotos} 
                      onPhotosUpdate={handlePhotosReplace}
                      data-testid="photos-gallery"
                      addButton={
                        contextPhotos.length >= 1 && uploadButtonProps ? (
                          <PhotoUploadButton
                            onClick={uploadButtonProps.onClick}
                            disabled={uploadButtonProps.disabled}
                          />
                        ) : undefined
                      }
                    />
                  )}
                  
                  {/* Optimized Photo Uploader */}
                  <OptimizedPhotoUploader 
                    onPhotosUpdate={handlePhotosUpdate}
                    existingPhotos={contextPhotos}
                    registerCleanup={registerPhotoUploaderCleanup}
                    onActiveWorkChange={setHasActiveUploadWork}
                    hideButton={contextPhotos.length >= 1}
                    onButtonPropsChange={setUploadButtonProps}
                  />
                  
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 md:p-4">
                    <div className="flex items-start gap-2 md:gap-3">
                      <div className="w-2 h-2 rounded-full bg-luxury-pinkBright mt-1.5 md:mt-2 flex-shrink-0" />
                      <div>
                        <h4 className="text-white font-medium mb-0.5 md:mb-1 text-sm">Dicas</h4>
                        <ul className="text-white/80 text-xs md:text-sm space-y-0.5 md:space-y-1">
                          <li>• Use fotos recentes e de boa qualidade</li>
                          <li>• Adicione pelo menos uma foto do rosto</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Validation feedback */}
            {photosRemaining > 0 && (
              <p className="text-yellow-500/80 text-xs md:text-sm text-center">
                ⚠️ Recomendamos adicionar pelo menos {MINIMUM_PHOTOS_REQUIRED} fotos
              </p>
            )}
          </div>
        </div>
      </StickyOnboardingFooter>

      {/* Skip Photos Dialog */}
      <SkipPhotosDialog
        isOpen={showSkipPhotosDialog}
        onClose={() => setShowSkipPhotosDialog(false)}
        onConfirmSkip={handleConfirmSkipPhotos}
        photosCount={contextPhotos.length}
      />
    </>
  );
};

export default PhotosStep;
